import React, { useState, useEffect } from 'react'
import { BiChevronLeft, BiChevronRight, BiSearch, BiRefresh } from 'react-icons/bi'

interface PaginationProps {
  currentPage: number
  limit: number
  total: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onSearch?: (searchTerm: string) => void
  onResetSearch?: () => void
  onRefresh?: () => void
  searchTerm?: string
  isLoading?: boolean
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage = 1,
  limit = 10,
  total = 0,
  onPageChange,
  onLimitChange,
  onSearch,
  onResetSearch,
  onRefresh,
  searchTerm = '',
  isLoading = false
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm)
  const [isSearching, setIsSearching] = useState(false)

  const totalPages = Math.ceil(total / limit)
  const start = total === 0 ? 0 : (currentPage - 1) * limit + 1
  const end = Math.min(currentPage * limit, total)

  // Update local search term when prop changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm)
  }, [searchTerm])

  const handleSearch = async () => {
    if (!onSearch) return
    
    const trimmedSearchTerm = localSearchTerm.trim()
    setIsSearching(true)
    try {
      await onSearch(trimmedSearchTerm)
    } finally {
      setIsSearching(false)
    }
  }

  const handleResetSearch = () => {
    setLocalSearchTerm('')
    onResetSearch?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    }
  }

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value)
    onLimitChange(newLimit)
  }

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = []
    const maxPagesToShow = 5
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      let startPage = Math.max(1, currentPage - 2)
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)
      
      if (endPage - startPage < maxPagesToShow - 1) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1)
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }
    }
    
    return pages
  }

  return (
    <div className="flex flex-col lg:flex-row items-center justify-between gap-4 p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
      {/* Left section - Action buttons and results per page */}
      <div className="flex items-center gap-3">
        {onResetSearch && (
          <button
            onClick={handleResetSearch}
            className="px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Reset Search
          </button>
        )}
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <BiRefresh className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        )}
        <select
          value={limit}
          onChange={handleLimitChange}
          className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="10">10 per page</option>
          <option value="20">20 per page</option>
          <option value="50">50 per page</option>
          <option value="100">100 per page</option>
        </select>
      </div>

      {/* Center section - Results info */}
      <div className="text-sm text-slate-600 dark:text-slate-400">
        Showing <span className="font-medium text-slate-900 dark:text-white">{start}</span> to{' '}
        <span className="font-medium text-slate-900 dark:text-white">{end}</span> of{' '}
        <span className="font-medium text-slate-900 dark:text-white">{total}</span> results
      </div>

      {/* Right section - Search and pagination */}
      <div className="flex items-center gap-3">
        {/* Search */}
        {onSearch && (
          <div className="flex items-center">
            <div className="relative">
              <input
                type="text"
                placeholder="Search agents..."
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-64 px-3 py-2 pr-10 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 disabled:opacity-50"
              >
                <BiSearch className={`w-4 h-4 ${isSearching ? 'animate-pulse' : ''}`} />
              </button>
            </div>
          </div>
        )}

        {/* Pagination controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1 || isLoading}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <BiChevronLeft className="w-4 h-4" />
          </button>

          {/* Page numbers */}
          {getPageNumbers().map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={isLoading}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                pageNum === currentPage
                  ? 'bg-primary-600 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              {pageNum}
            </button>
          ))}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages || isLoading}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <BiChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default Pagination
